  <sharepoint-site-selector>
  <div class="sharepoint-site-selector-container w-full flex flex-col gap-3">
    <!-- Header. Matches the Google Drive selector: a plain heading rather than
         a card, so the list of choices starts near the top. -->
    <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <h3 class="text-base font-semibold flex items-center gap-2">
        <i class="icon-business text-info" aria-hidden="true"></i>
        {locale?.t('sharepoint_selectsite_title')}
      </h3>
      <p class="text-xs text-base-content/70">
        {locale?.t(props.mode === 'folder' ? 'sharepoint_selectsite_description_folder' : 'sharepoint_selectsite_description')}
      </p>
    </div>

    <!-- Loading State -->
    <div if="{state.loading}" class="flex justify-center py-12">
      <div class="flex flex-col items-center gap-3">
        <span class="loading loading-spinner loading-lg"></span>
        <p class="text-sm text-base-content/80">{locale?.t('sharepoint_loadingsites')}</p>
      </div>
    </div>

    <!-- Error State -->
    <div if="{state.error && !state.loading}" class="alert alert-error">
      <i class="icon-warning text-xl"></i>
      <span class="text-sm">{state.error}</span>
      <button onclick="{loadSites}" class="btn btn-sm btn-ghost">
        <i class="icon-refresh"></i>
        {locale?.t('retry')}
      </button>
    </div>

    <!-- Search/Filter Toolbar -->
    <div if="{!state.loading && !state.error && state.allSites.length > 0}" class="flex gap-2 items-center">
      <input
        id="sharepoint-site-selector-search"
        type="search"
        placeholder="{locale?.t('sharepoint_searchsites_placeholder')}"
        aria-label="{locale?.t('sharepoint_searchsites_placeholder')}"
        class="input input-bordered input-sm flex-1"
        value="{state.searchQuery}"
        oninput="{(e) => handleSearch(e.target.value)}"
        onsearch="{(e) => handleSearch(e.target.value)}" />

      <button
        onclick="{clearFilter}"
        class="btn btn-sm btn-outline"
        disabled="{!state.searchQuery}">
        <i class="icon-clear me-1"></i>
        {locale?.t('clearfilter')}
      </button>
    </div>

    <!-- Sites List Container with Scrollable Area -->
    <div if="{!state.loading && !state.error && state.allSites.length > 0}" class="sites-list-container flex flex-col gap-0">
      <!-- Scrollable Sites List -->
      <div class="sites-list-scroll space-y-1.5 overflow-y-auto" style="max-height: 400px;">
        <!-- One click selects the site and opens it - there is no separate
             confirm step, so each row is a native button rather than a radio
             in a card. The amber/blue background wash the rows used to carry
             is gone: the My OneDrive / Team Site badge already says which is
             which, and the wash only made the two pickers look unrelated. -->
        <button each="{site in state.sites}"
                type="button"
                class="site-item w-full text-start bg-base-100 rounded-lg border border-base-300 hover:border-info hover:bg-base-200 transition-colors overflow-hidden disabled:opacity-60"
                disabled="{state.savingSelection}"
                aria-busy="{state.savingSiteId === site.id}"
                onclick="{() => selectSite(site)}">

        <div class="flex items-center gap-3 w-full py-2 px-3">
          <!-- Leading glyph, matching the slot the Google Drive rows use for
               their tinted drive icon. SharePoint sites carry no color of their
               own, so this one stays muted; it is here so the two lists have the
               same shape. Decorative - the name and badge identify the site. -->
          <i class="icon-business text-lg flex-shrink-0 text-base-content/40" aria-hidden="true"></i>

          <!-- Site Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-sm text-base-content truncate">{site.displayName || site.name}</span>
              <span if="{site.isPersonalSite}" class="badge badge-warning badge-xs flex-shrink-0">{locale?.t('sharepoint_myonedrive')}</span>
              <span if="{!site.isPersonalSite}" class="badge badge-info badge-xs flex-shrink-0">{locale?.t('sharepoint_teamsite')}</span>
            </div>
            <p if="{siteMeta(site)}" class="text-xs text-base-content/70 truncate">{siteMeta(site)}</p>
          </div>

          <span if="{state.savingSiteId === site.id}" class="loading loading-spinner loading-xs flex-shrink-0"></span>
          <i if="{state.savingSiteId !== site.id}" class="icon-arrow_forward text-base-content/50 flex-shrink-0" aria-hidden="true"></i>
        </div>
      </button>
      </div>

    <!-- Empty State (no sites at all) -->
    <div if="{!state.loading && !state.error && state.allSites.length === 0}" class="alert alert-warning">
      <i class="icon-error_outline text-xl"></i>
      <span class="text-sm">{locale?.t('sharepoint_nosites_error')}</span>
    </div>

    <!-- No Search Results -->
    <div if="{!state.loading && !state.error && state.allSites.length > 0 && state.sites.length === 0 && state.searchQuery}" class="card bg-base-100 mb-6">
      <div class="card-body text-center py-6">
        <i class="icon-search text-6xl text-gray-500 mb-2"></i>
        <p class="text-gray-500">{locale?.t('sharepoint_nosearchresults', { searchQuery: state.searchQuery })}</p>
      </div>
    </div>

    </div>

    <!-- OAuth Reauthorization Modal -->
    <div if="{state.showOAuthModal}" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">
          <i class="icon-warning text-warning me-2"></i>
          {locale?.t('sharepoint_oauth_connectionexpired')}
        </h3>
        <p class="py-4">{state.oauthErrorMessage}</p>
        <div class="modal-action gap-2">
          <button
            onclick="{closeOAuthModal}"
            class="btn btn-ghost">
            {locale?.t('cancel')}
          </button>
          <button
            onclick="{reauthorizeRepository}"
            class="btn btn-primary">
            <i class="icon-refresh me-1"></i>
            {locale?.t('sharepoint_oauth_reauthorizenow')}
          </button>
        </div>
      </div>
      <button type="button" aria-label="{locale?.t('close')}" class="modal-backdrop" onclick="{closeOAuthModal}"></button>
    </div>
  </div>

  <script>
    import { getRepositoryService } from "../services/service-loader";

    export default {
      props: {
        repositoryId: null,
        mode: 'file'
      },

      state: {
        sites: [],
        allSites: [],
        selectedSite: null,
        loading: false,
        error: null,
        savingSelection: false,
        // Which row is mid-save, so the spinner lands on the site that was
        // clicked rather than on the list as a whole.
        savingSiteId: null,
        sitesLoaded: false,
        currentSite: null,
        showOAuthModal: false,
        oauthErrorMessage: '',
        oauthErrorCallback: null,
        searchQuery: ''
      },

      async onMounted() {
        // Only load sites once per component instance
        if (!this.state.sitesLoaded) {
          await this.loadSites();
          this.state.sitesLoaded = true;
          this.update();
        }
      },

      async loadSites() {
        try {
          this.state.loading = true;
          this.state.error = null;
          this.update();

          const repositoryService = await getRepositoryService();
          const response = await repositoryService.getSharePointSites(this.props.repositoryId);
          const sites = response.sites || [];
          
          if (!sites || sites.length === 0) {
            this.state.sites = [];
            this.state.allSites = [];
            this.state.error = this.locale.t('sharepoint_nosites_error');
            this.state.loading = false;
          } else {
            // Do NOT auto-select - require user to explicitly select a site
            this.state.sites = sites;
            this.state.allSites = sites;
            this.state.selectedSite = null;
            this.state.error = null;
            this.state.loading = false;
          }
          
          this.update();
        } catch (err) {
          console.error('Failed to load SharePoint sites:', err);
          
          // Check if it's an OAuth token expiration error
          if (err.response?.status === 401 && err.response?.data?.error === 'oauth_token_expired') {
            this.showOAuthExpiredModal(
              err.response.data.message || 'Your repository connection has expired.',
              () => this.loadSites()
            );
            this.state.loading = false;
            this.update();
            return;
          }
          
          this.state.error = err.message || 'Failed to load SharePoint sites. Please try again.';
          this.state.loading = false;
          this.state.sites = [];
          this.state.allSites = [];
          this.update();
        }
      },

      /**
       * The muted second line of a row: description, address and quota joined
       * into one line rather than stacked as three. This is what makes the row
       * two lines instead of four or five.
       */
      siteMeta(site) {
        const parts = [];
        if (site.description) parts.push(site.description);
        if (site.webUrl) parts.push(site.webUrl);
        if (site.quota) {
          parts.push(`${this.formatSize(site.quota.used)} / ${this.formatSize(site.quota.total)}`);
        }
        return parts.filter(Boolean).join(' · ');
      },

      /**
       * Selecting a site opens it. There is no separate confirm step: the old
       * two-stage flow (pick a row, then press Browse Files) asked for a
       * second click that carried no extra decision.
       */
      async selectSite(site) {
        if (!site || this.state.savingSelection) return;

        // Capture context before async operations
        const context = this;
        const selectedSite = site;
        const repositoryId = this.props.repositoryId;

        try {
          context.state.selectedSite = site;
          context.state.savingSelection = true;
          context.state.savingSiteId = site.id;
          context.update();

          console.log('[SharePoint Site Selector] Saving site selection:', selectedSite.id);

          const repositoryService = await getRepositoryService();
          const response = await repositoryService.selectSharePointSite(
            repositoryId,
            selectedSite.id
          );
          
          console.log('[SharePoint Site Selector] Site selection saved:', response);
          
          // FIX: Extract driveId from API response - this is CRITICAL for all SharePoint file operations
          const driveId = response.repository?.driveId || selectedSite.driveId;
          
          if (!driveId) {
            console.error('[SharePoint Site Selector] CRITICAL: No driveId in response!', response);
            alert(context.locale.t('sharepoint_error_nodriveid'));
            context.state.savingSelection = false;
            context.state.savingSiteId = null;
            context.update();
            return;
          }
          
          console.log('[SharePoint Site Selector] Emitting site-selected event with driveId:', driveId);
          
          context.state.savingSelection = false;
          
          context.state.savingSiteId = null;
          context.update();
          
          // Emit event to parent component (file-picker) with proper data structure including driveId
          const eventData = {
            siteId: selectedSite.id,
            driveId: driveId,
            selectedSite: {
              id: selectedSite.id,
              name: selectedSite.name,
              displayName: selectedSite.displayName || selectedSite.name,
              webUrl: selectedSite.webUrl,
              isPersonalSite: selectedSite.isPersonalSite,
              driveId: driveId
            }
          };
          
          // Use global riot object if available, otherwise try direct emit
          if (context.$root && context.$root.$emit) {
            context.$root.$emit('site-selected', eventData);
          } else {
            console.warn('[SharePoint Site Selector] $root.$emit not available, trying window event');
            // Fallback: dispatch custom event on window
            window.dispatchEvent(new CustomEvent('sharepoint-site-selected', {
              detail: eventData
            }));
          }
        } catch (error) {
          console.error('Failed to save SharePoint site selection:', error);
          
          // Check if it's an OAuth token expiration error
          if (error.response?.status === 401 && error.response?.data?.error === 'oauth_token_expired') {
            context.state.savingSelection = false;
            context.state.savingSiteId = null;
            context.showOAuthExpiredModal(
              error.response.data.message || 'Your repository connection has expired.',
              () => context.selectSite(selectedSite)
            );
            context.update();
            return;
          }
          
          alert(context.locale.t('sharepoint_error_savefailed') + ': ' + (error.message || 'Unknown error'));
          context.state.savingSelection = false;
          context.state.savingSiteId = null;
          context.update();
        }
      },

      showOAuthExpiredModal(errorMessage, retryCallback) {
        // Show OAuth re-auth modal and store the callback to retry the original action
        this.state.showOAuthModal = true;
        this.state.oauthErrorMessage = errorMessage || this.locale.t('sharepoint_oauth_defaultmessage');
        this.state.oauthErrorCallback = retryCallback;
        this.update();
      },

      async reauthorizeRepository() {
        try {
          const repositoryService = await getRepositoryService();

          // Get OAuth URL
          const oauthData = await repositoryService.getOAuthUrl(this.props.repositoryId);
          
          if (oauthData?.authorizationUrl) {
            // Create a promise that resolves when OAuth completes
            const oauthPromise = new Promise((resolve) => {
              const messageHandler = (event) => {
                // Verify message is from our origin
                if (event.origin !== window.location.origin) return;
                
                // Check for OAuth completion message
                if (event.data?.type === 'repository-connected') {
                  window.removeEventListener('message', messageHandler);
                  resolve(event.data);
                }
              };
              
              // Listen for postMessage from OAuth callback popup
              window.addEventListener('message', messageHandler);
              
              // Timeout after 5 minutes if no response
              setTimeout(() => {
                window.removeEventListener('message', messageHandler);
                resolve({ success: false, timeout: true });
              }, 5 * 60 * 1000);
            });
            
            // Open OAuth popup
            window.open(oauthData.authorizationUrl, 'oauth-popup', 'width=500,height=600');
            
            // Wait for OAuth callback
            const result = await oauthPromise;
            
            // Close modal
            this.state.showOAuthModal = false;
            this.update();
            
            if (result.success !== false) {
              // OAuth succeeded, retry the original action
              const callback = this.state.oauthErrorCallback;
              if (callback && typeof callback === 'function') {
                await callback();
              }
            } else if (result.timeout) {
              alert(this.locale.t('sharepoint_oauth_error_timeout'));
            } else {
              alert(this.locale.t('sharepoint_oauth_error_failed') + ': ' + (result.error || 'Unknown error'));
            }
          } else {
            alert(this.locale.t('sharepoint_oauth_error_nourl'));
          }
        } catch (error) {
          console.error('Failed to re-authorize:', error);
          alert(this.locale.t('sharepoint_oauth_error_reauth') + ': ' + (error.message || 'Unknown error'));
        }
      },

      closeOAuthModal() {
        this.state.showOAuthModal = false;
        this.update();
      },

      handleSearch(query) {
        this.state.searchQuery = query;
        this.update();
        this.filterSites(query);
      },

      clearFilter() {
        this.state.searchQuery = '';
        this.update();
        this.filterSites('');
      },

      filterSites(query) {
        const searchQuery = query.toLowerCase();
        if (!searchQuery) {
          // No query - show all sites
          this.state.sites = this.state.allSites;
          this.update();
          return;
        }

        // Filter sites by name, displayName, or description
        const filteredSites = this.state.allSites.filter(site =>
          site.name?.toLowerCase().includes(searchQuery) ||
          site.displayName?.toLowerCase().includes(searchQuery) ||
          site.description?.toLowerCase().includes(searchQuery)
        );
        this.state.sites = filteredSites;
        this.update();
      },

      formatSize(bytes) {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
      }
    }
  </script>

  <style>
    .sharepoint-site-selector-container {
      font-family: inherit;
    }

    /* Container for scrollable sites list */
    .sites-list-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    /* Scrollable area with max height */
    .sites-list-scroll {
      flex: 1;
      overflow-y: auto;
      padding-right: 8px; /* Account for scrollbar */
    }

    .site-item {
      display: block;
    }
  </style>
</sharepoint-site-selector>
